import { s } from '@/theme/tokens';
import { FormCard } from '@/ui/composites/FormCard';
import { Inline } from '@/ui/layout/Inline';
import { Dialog, DialogContent, type DialogProps } from '@/ui/primitives';
import { Box } from '@/ui/primitives/Box';
import type { SpacingToken } from '@/theme/tokens';
import type { SxProps, Theme } from '@/ui/primitives';
import type { KeyboardEventHandler, ReactElement, ReactNode } from 'react';

type DialogLayoutProps = {
  open: boolean;
  title?: string;
  titleSlot?: ReactNode;
  titleSx?: SxProps<Theme>;
  description?: ReactNode;
  actions?: ReactNode;
  padding?: SpacingToken | null;
  headerGap?: SpacingToken;
  contentOffset?: SpacingToken;
  actionsAlign?: 'flex-start' | 'center' | 'flex-end' | 'space-between';
  actionsGap?: SpacingToken;
  footerGap?: SpacingToken;
  contentGap?: SpacingToken;
  gap?: SpacingToken;
  dialogSx?: DialogProps['sx'];
  contentSx?: DialogProps['sx'];
  surfaceVariant?: 'default' | 'settings';
  onContentKeyDownCapture?: KeyboardEventHandler<HTMLDivElement>;
  onClose: () => void;
  disableClose?: boolean;
  allowTopBarInteraction?: boolean;
  maxWidth?: DialogProps['maxWidth'];
  scroll?: DialogProps['scroll'];
  children: ReactNode;
};

export const DialogLayout = ({
  open,
  title,
  titleSlot,
  titleSx,
  description,
  actions,
  padding,
  headerGap = 'sm',
  actionsAlign = 'flex-end',
  actionsGap = 'md',
  footerGap = 'xs',
  contentGap = 'md',
  gap = 'md',
  dialogSx,
  contentSx,
  surfaceVariant = 'settings',
  contentOffset = 'md',
  onContentKeyDownCapture,
  onClose,
  disableClose = false,
  allowTopBarInteraction = false,
  maxWidth = 'sm',
  scroll = 'paper',
  children,
}: DialogLayoutProps): ReactElement => {
  const baseContentSx: DialogProps['sx'] = (theme) => ({
    overflow: 'visible',
    backgroundColor: `${theme.app.colors.surface} !important`,
  });
  const resolvedContentSx: DialogProps['sx'] = contentSx
    ? ([
        baseContentSx,
        ...(Array.isArray(contentSx) ? contentSx : [contentSx]),
      ] as DialogProps['sx'])
    : baseContentSx;
  const contentSxArray = Array.isArray(resolvedContentSx)
    ? resolvedContentSx
    : ([resolvedContentSx] as Array<DialogProps['sx']>);
  const footer = actions ? (
    <Inline gap={actionsGap} justify={actionsAlign}>
      {actions}
    </Inline>
  ) : undefined;
  const dialogSlotProps: DialogProps['slotProps'] = disableClose
    ? {
        paper: { sx: { p: s('none') } },
        backdrop: {
          onClick: (event) => {
            event.stopPropagation();
          },
        },
      }
    : { paper: { sx: { p: s('none') } } };
  const topBarOffsetSx: DialogProps['sx'] | undefined = allowTopBarInteraction
    ? {
        zIndex: 9,
        '& .MuiBackdrop-root': {
          top: 'var(--app-topbar-height, 54px)',
          height: 'calc(100% - var(--app-topbar-height, 54px))',
        },
        '& .MuiDialog-container': {
          top: 'var(--app-topbar-height, 54px)',
          height: 'calc(100% - var(--app-topbar-height, 54px))',
          maxHeight: 'calc(100% - var(--app-topbar-height, 54px))',
        },
      }
    : undefined;
  const hasDialogSx = Boolean(topBarOffsetSx) || Boolean(dialogSx);
  const resolvedDialogSx = hasDialogSx
    ? ([topBarOffsetSx, dialogSx].filter(Boolean) as DialogProps['sx'])
    : undefined;

  return (
    <Dialog
      open={open}
      onClose={disableClose ? undefined : onClose}
      disableEscapeKeyDown={disableClose}
      fullWidth
      maxWidth={maxWidth}
      scroll={scroll}
      slotProps={dialogSlotProps}
      sx={resolvedDialogSx}
    >
      <DialogContent
        sx={[{ p: s('none') }, ...contentSxArray]}
        onKeyDownCapture={onContentKeyDownCapture}
      >
        <FormCard
          container="none"
          title={title}
          titleSlot={titleSlot}
          titleSx={titleSx}
          description={description}
          padding={padding}
          headerGap={headerGap}
          footer={footer}
          footerGap={footerGap}
          contentGap={contentGap}
          gap={gap}
          surfaceVariant={surfaceVariant}
        >
          <Box sx={{ mt: s(contentOffset) }}>{children}</Box>
        </FormCard>
      </DialogContent>
    </Dialog>
  );
};
